import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TablePagination,
  Button,
  Modal,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import CustomAlert from "../../components/UI/CustomAlert/CustomAlert";
import FeeDetailsPanel from "../../components/FeeDetailsPanel/FeeDetailsPanel";
import { purchaseFeeTableList, purchaseFeeTableAdd, purchaseFeeTableEdit } from "../../util/API/loans";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f8f9fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
    fontSize: "15px",
    padding: "16px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    padding: "16px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#f9f9f9",
  },
  "&:hover": {
    backgroundColor: "#f0f4ff !important",
  },
}));

function Fees() {
  const [feedata, setFeedata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add Fee Modal State
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newFee, setNewFee] = useState({
    description: "",
    active: true,
    merchantfeeincludedinamount: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Edit Fee Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [editForm, setEditForm] = useState({
    tableid: "",
    description: "",
    active: true,
    merchantfeeincludedinamount: false,
  });

  // Detail Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState("");

  // Global Alert State
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    header: "",
    message: "",
  });

  // Search State
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    getFeeData();
  }, []);

  const getFeeData = async () => {
    console.log("Fetching fee data...");
    setLoading(true);

    try {
      const response = await purchaseFeeTableList("", 0);

      console.log("Full API Response:", response);

      if (response.callStatus === "1") {
        const rawRecords = response.payload?.record || [];
        console.log("Raw records from API:", rawRecords);

        const feeArray = Array.isArray(rawRecords) ? rawRecords : rawRecords ? [rawRecords] : [];

        const processedFees = feeArray
          .map((item, index) => {
            const fee = item?.Purchasefeetable;
            if (!fee) {
              console.warn(`Missing Purchasefeetable at index ${index}`, item);
              return null;
            }
            return fee;
          })
          .filter(Boolean);

        console.log("Processed fees:", processedFees);
        processedFees.sort((a, b) => Number(b.tableid) - Number(a.tableid));
        setFeedata(processedFees);
      } else {
        console.warn("API returned no data or error:", response);
        setFeedata([]);
      }
    } catch (error) {
      console.error("Fee API Error:", error);
      setFeedata([]);
      setAlert({
        showAlert: true,
        severity: "error",
        header: "Error",
        message: "Failed to load fees. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusLabel = (active) => (active == "1" ? "Active" : "Inactive");
  const getStatusColor = (active) => (active == "1" ? "success" : "default");

  const getIncludedLabel = (included) => (included == "1" ? "Yes" : "No");
  const getIncludedColor = (included) => (included == "1" ? "primary" : "default");

  // Add Fee Modal Handlers
  const handleOpenAddModal = () => {
    setNewFee({
      description: "",
      active: true,
      merchantfeeincludedinamount: false,
    });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleAddFee = async () => {
    if (!newFee.description.trim()) {
      setAlert({
        showAlert: true,
        severity: "warning",
        header: "Validation Error",
        message: "Fee description is required",
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await purchaseFeeTableAdd({
        location: "",
        description: newFee.description.trim(),
        active: newFee.active ? 1 : 0,
        merchantfeeincludedinamount: newFee.merchantfeeincludedinamount ? 1 : 0,
      });

      console.log("Add Fee Response:", response);

      if (response.callStatus === "1") {
        setAlert({
          showAlert: true,
          severity: "success",
          header: "Success",
          message: "New fee added successfully!",
        });
        handleCloseAddModal();
        getFeeData();
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          header: "Error",
          message: response.msg || "Failed to add fee",
        });
      }
    } catch (error) {
      console.error("Add Fee Error:", error);
      setAlert({
        showAlert: true,
        severity: "error",
        header: "Network Error",
        message: "Failed to add fee. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Edit Fee Modal Handlers
  const handleOpenEditModal = (fee) => {
    setEditingFee(fee);
    setEditForm({
      tableid: fee.tableid,
      description: fee.description || "",
      active: fee.active == "1",
      merchantfeeincludedinamount: fee.merchantfeeincludedinamount == "1",
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingFee(null);
  };

  const handleEditFee = async () => {
    if (!editForm.description.trim()) {
      setAlert({
        showAlert: true,
        severity: "warning",
        header: "Validation Error",
        message: "Fee description is required",
      });
      return;
    }

    setSubmitting(true);

    try {
      const response = await purchaseFeeTableEdit({
        location: "",
        tableid: editForm.tableid,
        description: editForm.description.trim(),
        active: editForm.active ? 1 : 0,
        merchantfeeincludedinamount: editForm.merchantfeeincludedinamount ? 1 : 0,
      });

      console.log("Edit Fee Response:", response);

      if (response.callStatus === "1") {
        setAlert({
          showAlert: true,
          severity: "success",
          header: "Success",
          message: "Fee updated successfully!",
        });
        handleCloseEditModal();
        getFeeData();
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          header: "Error",
          message: response.msg || "Failed to update fee",
        });
      }
    } catch (error) {
      console.error("Edit Fee Error:", error);
      setAlert({
        showAlert: true,
        severity: "error",
        header: "Network Error",
        message: "Failed to update fee. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Detail Modal Handlers
  const handleOpenDetailModal = (fee) => {
    setSelectedTableId(fee.tableid);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTableId("");
  };

  // Filtered data with search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return feedata;

    const lowerSearch = searchTerm.toLowerCase();
    return feedata.filter((fee) => {
      return (
        fee.tableid?.toString().toLowerCase().includes(lowerSearch) ||
        fee.description?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [feedata, searchTerm]);

  const displayedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalCount = filteredData.length;

  return (
    <Box className="page-container admin-container">
      <Box className="header-container" sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h5" className="admin-header" sx={{ fontWeight: 600 }}>
            Fee Configuration Table
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Manage fee configurations for loan products
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddModal}
          sx={{
            backgroundColor: "#5523D2",
            "&:hover": { backgroundColor: "#4419a8" },
            textTransform: "none",
            fontWeight: "bold",
          }}
        >
          Add New Fee
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by Table ID or Description..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#888" }} />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          sx={{
            maxWidth: 600,
            "& .MuiOutlinedInput-root": {
              borderRadius: "30px",
              backgroundColor: "#f9f9f9",
            },
          }}
        />
      </Box>

      <Box className="admin-table-container">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
            <CircularProgress size={60} thickness={5} />
            <Typography sx={{ ml: 2 }}>Loading fees...</Typography>
          </Box>
        ) : totalCount === 0 ? (
          <Box sx={{ textAlign: "center", py: 12, color: "#666" }}>
            <Typography variant="h6" gutterBottom>
              {searchTerm ? "No fees match your search" : "No fees configured"}
            </Typography>
            <Typography variant="body2">
              {searchTerm ? "Try a different Table ID or description." : 'Click "Add New Fee" to create your first fee entry.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Table ID</StyledTableCell>
                    <StyledTableCell>Fee Description</StyledTableCell>
                    <StyledTableCell align="center">Status</StyledTableCell>
                    <StyledTableCell align="center">Included in Loan Amount</StyledTableCell>
                    <StyledTableCell align="center">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedData
                    .map((fee) => (
                      <StyledTableRow key={fee.tableid}>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#5523D2" }}>
                            {fee.tableid}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {fee.description || "(No description)"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusLabel(fee.active)}
                            color={getStatusColor(fee.active)}
                            size="small"
                            sx={{ minWidth: 80, fontWeight: "bold" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getIncludedLabel(fee.merchantfeeincludedinamount)}
                            color={getIncludedColor(fee.merchantfeeincludedinamount)}
                            size="small"
                            sx={{ minWidth: 60, fontWeight: "bold" }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditModal(fee)}
                              sx={{ color: "#5523D2" }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetailModal(fee)}
                              sx={{ color: "#666" }}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </Box>

      {/* Detail Modal */}
      <Modal open={detailModalOpen} onClose={handleCloseDetailModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 800, md: 1000 },
            maxWidth: "95vw",
            maxHeight: "92vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 4,
            overflow: "hidden",
            p: { xs: 2, sm: 4 },
          }}
        >
          <FeeDetailsPanel 
            tableid={selectedTableId} 
            onClose={handleCloseDetailModal}
            onRefresh={getFeeData}
          />
        </Box>
      </Modal>

      {/* Add Fee Modal */}
      <Modal open={addModalOpen} onClose={handleCloseAddModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
            Add New Fee
          </Typography>

          <TextField
            fullWidth
            label="Fee Description"
            value={newFee.description}
            onChange={(e) => setNewFee({ ...newFee, description: e.target.value })}
            sx={{ mb: 3 }}
            required
          />

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={newFee.active}
                  onChange={(e) => setNewFee({ ...newFee, active: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={newFee.merchantfeeincludedinamount}
                  onChange={(e) => setNewFee({ ...newFee, merchantfeeincludedinamount: e.target.checked })}
                  color="primary"
                />
              }
              label="Included in Loan Amount"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddFee}
              disabled={submitting || !newFee.description.trim()}
              sx={{ backgroundColor: "#5523D2" }}
            >
              {submitting ? "Adding..." : "Add Fee"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Edit Fee Modal */}
      <Modal open={editModalOpen} onClose={handleCloseEditModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
            Edit Fee
          </Typography>

          <TextField
            fullWidth
            label="Fee Description"
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            sx={{ mb: 3 }}
            required
          />

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.active}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.merchantfeeincludedinamount}
                  onChange={(e) => setEditForm({ ...editForm, merchantfeeincludedinamount: e.target.checked })}
                  color="primary"
                />
              }
              label="Included in Loan Amount"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditFee}
              disabled={submitting || !editForm.description.trim()}
              sx={{ backgroundColor: "#5523D2" }}
            >
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Global CustomAlert */}
      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={() => setAlert({ ...alert, showAlert: false })}
        autoclose={alert.severity === "success"}
      />
    </Box>
  );
}

export default Fees;